"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.areSurveysCompleted = areSurveysCompleted;
exports.getCanEditScreenTimerFromMission = getCanEditScreenTimerFromMission;
exports.getContextualData = getContextualData;
exports.getCurrentObjectiveTimerId = getCurrentObjectiveTimerId;
exports.getCurrentVocalTrackFromMission = getCurrentVocalTrackFromMission;
exports.getCurrentVocalTracksFromMission = getCurrentVocalTracksFromMission;
exports.getDeathStatusFromMission = getDeathStatusFromMission;
exports.getIsDeathHappeningFromMission = getIsDeathHappeningFromMission;
exports.getIsMissionPauseActiveFromMission = getIsMissionPauseActiveFromMission;
exports.getIsPausedFromMission = getIsPausedFromMission;
exports.getIsScreenLoopingFromMission = getIsScreenLoopingFromMission;
exports.getIsTrainingFromMission = getIsTrainingFromMission;
exports.getMainObjectiveFromMission = getMainObjectiveFromMission;
exports.getOverlayFromMission = getOverlayFromMission;
exports.getPausedStateKeyFromMission = getPausedStateKeyFromMission;
exports.getPhaseFromMission = getPhaseFromMission;
exports.getScreenFromMission = getScreenFromMission;
exports.getShipHealthFromMission = getShipHealthFromMission;
exports.getStudentsByTeamId = getStudentsByTeamId;
exports.getStudentsWithSurveysFromMission = getStudentsWithSurveysFromMission;
exports.getTeacherResponseFromMission = getTeacherResponseFromMission;
exports.getTeamForStudent = getTeamForStudent;
exports.shouldShowGeneratingAnalyticsFromMission = shouldShowGeneratingAnalyticsFromMission;
exports.shouldShowSequenceFrameFromMission = shouldShowSequenceFrameFromMission;
exports.shouldShowSurveyFromMission = shouldShowSurveyFromMission;
var _missionStateConstants = require("../constants/missionStateConstants");
var _students = require("./students");
var _lodash = require("lodash");
/**
 * Gets the current ship health (if mission state doesn't exist returns 0)
 *
 * @param {?SharedMissionStateSelectorState} missionState
 * @return {number}
 */
function getShipHealthFromMission(missionState) {
  return missionState?.shipHealth || 0;
}

/**
 * Get current main objective: a main objective string and an explanation of the
 * main objective for a teacher
 * @param {?SharedMissionStateSelectorState} missionState
 * @returns { teacherInfo: string, objectiveText: string }
 */
function getMainObjectiveFromMission(missionState) {
  const defaultObject = {
    teacherInfo: '',
    objectiveText: _missionStateConstants.NO_OBJECTIVE
  };
  return missionState?.launchpad.mainObjective || defaultObject;
}

/**
 * Whether or not the mission is paused
 * @param {?SharedMissionStateSelectorState} missionState
 * @returns { boolean }
 */
function getIsPausedFromMission(missionState) {
  return missionState?.pausedState.state === 'PAUSED';
}

/**
 * Whether or not the mission pause is active
 * @param {?SharedMissionStateSelectorState} missionState
 * @returns { boolean }
 */
function getIsMissionPauseActiveFromMission(missionState) {
  return !!missionState?.pausedState.missionPause;
}

/**
 * the pause state of the mission
 * @param {?SharedMissionStateSelectorState} missionState
 * @returns { PausedStateKey }
 */
function getPausedStateKeyFromMission(missionState) {
  return missionState?.pausedState.state;
}

/**
 * Gets the current phase the mission is in.
 * @param {?SharedMissionStateSelectorState} missionState
 * @returns {?$Keys<typeof MISSION_PHASES>}
 */
function getPhaseFromMission(missionState) {
  return missionState?.missionPhase;
}

/**
 * Whether or not the mission is in its training phase
 * @param {?SharedMissionStateSelectorState} missionState
 * @returns { boolean }
 */
function getIsTrainingFromMission(missionState) {
  return getPhaseFromMission(missionState) === _missionStateConstants.MISSION_PHASES.TRAINING;
}

/**
 * Whether or not everyone has died on a mission
 * @param {?SharedMissionStateSelectorState} missionState
 * @returns {boolean}
 */
function getIsDeathHappeningFromMission(missionState) {
  return missionState?.death.status === 'SHIP_DEATH' || missionState?.death.status === 'FAILED_STATE';
}

/**
 * Get the death state on a mission
 * @param {?SharedMissionStateSelectorState} missionState
 * @returns {CrewDeathStatus}
 */
function getDeathStatusFromMission(missionState) {
  return missionState?.death;
}

/**
 * Gets all the current vocal tracks on a mission
 * @param {?SharedMissionStateSelectorState} missionState
 * @returns {VocalTrack[]}
 */
function getCurrentVocalTracksFromMission(missionState) {
  return missionState?.launchpad.vocalTracks || [];
}

/**
 * Gets the current overlay to display on a mission
 * @param {?SharedMissionStateSelectorState} missionState
 * @returns {?Overlay}
 */
function getOverlayFromMission(state) {
  return state?.launchpad.overlays[0] || null;
}

/**
 * Gets the current screen to display on a mission
 * @param {?SharedMissionStateSelectorState} missionState
 * @returns {?Screen}
 */
function getScreenFromMission(state) {
  return state?.launchpad.screen;
}

/**
 * Gets whether or not we can change the timer of the screen.
 * We cannot add/subtract time from non-looping videos or the animation portion of culminating moment screens.
 * All other screens can be changed.
 */
function getCanEditScreenTimerFromMission(state) {
  const screen = getScreenFromMission(state);
  if (!screen) {
    return false;
  }
  if (screen.type === 'VIDEO') {
    return getIsScreenLoopingFromMission(state);
  } else if (screen.type === 'CULMINATING_MOMENT' && screen.result) {
    return false;
  } else if (screen.type === 'COLLABORATIVE_CULMINATING_MOMENT_SCREEN') {
    return screen.currentStep.status === _missionStateConstants.COLLABORATIVE_CULMINATING_MOMENT.SCREEN_STATUS.EDITING;
  }
  return true;
}

/**
 * Gets the vocal track that should currently be playing. There could be an existing vocal track that shouldn't play yet. If that is the case,
 * this selector will return null
 * @param {?SharedMissionStateSelectorState}
 * @returns {?VocalTrack}
 */
function getCurrentVocalTrackFromMission(state) {
  const tracks = getCurrentVocalTracksFromMission(state);
  return tracks.length > 0 ? tracks[0] : null;
}

/**
 * Whether or not the current screen should loop
 * @param {?SharedMissionStateSelectorState} missionState
 * @returns {boolean}
 */
function getIsScreenLoopingFromMission(missionState) {
  if (!missionState) {
    return false;
  }
  if (missionState.launchpad.screen) {
    const screen = missionState.launchpad.screen;
    if (screen.type === 'VIDEO') {
      return screen.looping;
    }
  }
  return false;
}

/**
 * Returns the entire teacher survey response
 * @param {SharedMissionStateSelectorState} state
 * @returns {?TeacherSurveyData}
 */
function getTeacherResponseFromMission(missionState) {
  return missionState?.surveys ? missionState.surveys.teacherResponse : null;
}

/**
 * Gets the student ids of those who have responded to the end of mission survey
 * @param {?SharedMissionStateSelectorState} missionState
 * @returns {Set<string>}
 */
function getStudentsWithSurveysFromMission(missionState) {
  if (!missionState || !missionState.surveys || !missionState.surveys.studentResponses) return new Set();
  return new Set(Object.keys(missionState.surveys.studentResponses));
}

/**
 * Whether or not the survey frame should be displayed
 *
 * @param {?SharedMissionStateSelectorState} missionState - the current full state of the mission
 *
 * @returns {boolean} - true if the survey collection progress should be displayed, false otherwise
 */
function shouldShowSurveyFromMission(missionState) {
  return getPhaseFromMission(missionState) === _missionStateConstants.MISSION_PHASES.SURVEY_STANDBY;
}

/**
 * Whether or not the end of mission result sequence frame should be displayed
 *
 * @param {boolean} analyticsAvailable - if the analytics are available to the mission
 * @param {?SharedMissionStateSelectorState} missionState - the current full state of the mission
 *
 * @returns {boolean} - true if the end of mission sequence should be displayed, false otherwise
 */
function shouldShowSequenceFrameFromMission(missionState, {
  analyticsAvailable
}) {
  const phase = getPhaseFromMission(missionState);
  return analyticsAvailable && phase === _missionStateConstants.MISSION_PHASES.REVIEW_RESULTS || phase === _missionStateConstants.MISSION_PHASES.CLOSED;
}

/**
 * Whether or not the generating analytics message should be shown
 *
 * @param {boolean} analyticsAvailable - if the analytics are available to the mission
 * @param {?SharedMissionStateSelectorState} missionState - the current full state of the mission
 *
 * @returns {boolean} - true if the generating analytics message should be displayed, false otherwise
 */
function shouldShowGeneratingAnalyticsFromMission(missionState, {
  analyticsAvailable
}) {
  return !analyticsAvailable && getPhaseFromMission(missionState) === _missionStateConstants.MISSION_PHASES.REVIEW_RESULTS;
}

/**
 * Gets the current objective timerId from the state.
 */
function getCurrentObjectiveTimerId(state) {
  const screen = getScreenFromMission(state);
  return screen ? screen.timerId : null;
}

/**
 * areSurveysCompleted - a selector for checking if all connected students have answered the surveys
 *
 * @param  {?BothFullMissionStates} state  - the current full state of the mission
 *
 * @returns boolean - true if all the connected students have completed the surveys, false otherwise
 */
function areSurveysCompleted(state) {
  return Object.keys((0, _students.getConnectedStudents)(state, false)).every(studentId => state?.surveys?.studentResponses.hasOwnProperty(studentId));
}

/**
 * Gets the team for a given student
 */
function getTeamForStudent(state, studentId) {
  const teamId = Object.keys(state.teams).find(teamId => state.teams[teamId].lead === studentId || state.teams[teamId].students.includes(studentId));
  return teamId ? state.teams[teamId] : null;
}

/**
 * Gets all students on a team
 *
 * @param state the current mission state
 * @param teamId the id of the team to get students for
 */
function getStudentsByTeamId(state, teamId) {
  const students = [];
  const team = state.teams[teamId];
  if (!team) return students;
  const simpleTeam = {
    id: team.id,
    displayName: team.displayName
  };
  if (team.lead) {
    students.push({
      ...state.students[team.lead],
      teamRole: 'lead',
      team: simpleTeam
    });
  }
  students.push(...team.students.map(studentId => ({
    ...state.students[studentId],
    teamRole: 'general',
    team: simpleTeam
  })));
  return students;
}

/**
 * Checks if the given `entity` meets the given `availability` requirements
 *
 * @return {boolean} - true if the entity meets the requirements, false otherwise
 */
function entityMeetsContextualDataAvailability(state, availability, entity) {
  if (entity === 'ANY' || availability === 'ALL' || entity.studentId && availability.students?.includes(entity.studentId)) {
    return true;
  }
  const teamId = entity.teamId || entity.studentId && getTeamForStudent(state, entity.studentId)?.id;
  return !!teamId && (availability.teams?.includes(teamId) ||
  // if we're not checking against a specific student, check against all students on the team
  !entity.studentId && getStudentsByTeamId(state, teamId).some(student => availability.students?.includes(student.id)));
}
/**
 * Gets contextual data for the given `context` and `entity`. Only gets the data that should be available for the given entity in the given context.
 *
 * @param state - the current mission state
 * @param context - the context to get contextual data for
 * @param entity - the student or team to get contextual data for. if `'ANY'`, gets contextual data that would be available to any student in the given context
 * @returns the contextual data
 *     base - contextual data that comes directly from the source (i.e. map/object data for maps, canvas data for creative canvas)
 *    extra - extra contextual data that is made available in this context from some other source
 */
function getContextualData(state, context, _entity) {
  const connectedStudents = new Set(Object.keys((0, _students.getConnectedStudents)(state)));
  const entity = _entity === 'ANY' || !_entity.studentId || !_entity.teamId ? _entity : {
    ..._entity,
    teamId: getTeamForStudent(state, _entity.studentId)?.id
  };
  const extraContextualData = state.extraContextualData.data.filter(dataEntry => dataEntry.appearance.some(appearance => {
    const isCorrectContext = context.type === 'MAP' ? appearance.type === 'MAP' && appearance.mapId === context.map._id : appearance.type === 'CREATIVE_CANVAS' && appearance.canvasActionId === context.canvasActionId;
    return isCorrectContext && entityMeetsContextualDataAvailability(state, appearance.availability, entity);
  })).map(({
    appearance,
    votes,
    ...dataEntry
  }) => {
    return {
      ...dataEntry,
      dataSource: {
        type: 'EXTRA_CONTEXTUAL_DATA'
      },
      studentVote: typeof entity === 'object' && entity.studentId && votes.students[entity.studentId]?.vote || null,
      classVote: Object.keys(votes.students).reduce((totalVotes, studentId) => {
        if (connectedStudents.has(studentId)) {
          return totalVotes + votes.students[studentId].vote;
        }
        return totalVotes;
      }, 0)
    };
  });
  const baseContextualData = [];
  if (context.type === 'MAP') {
    baseContextualData.push(...getContextualDataForMap({
      state,
      entity,
      map: context.map,
      includeHiddenObjects: context.includeHiddenObjects,
      includeUnscannedObjects: context.includeUnscannedObjects,
      connectedStudents
    }));
  } else {
    const {
      base,
      extra
    } = getContextualDataForCreativeCanvas({
      state,
      entity,
      canvasId: context.canvasActionId,
      maps: context.maps,
      connectedStudents
    });
    baseContextualData.push(...base);
    extraContextualData.push(...extra);
  }
  return {
    base: baseContextualData,
    extra: (0, _lodash.uniqBy)(extraContextualData, 'id')
  };
}

/**
 * Gets contextual data for the given `map`.
 *
 * @param state - the current mission state
 * @param map - the map to get contextual data for
 * @param entity - the entity to get contextual data for. If `'ANY'`, gets contextual data on the map that would be available to any student
 * @param includeHiddenObjects - whether or not to include data from hidden objects
 * @param includeUnscannedObjects - whether or not to include data from unscanned objects
 */
function getContextualDataForMap({
  state,
  map,
  entity,
  includeHiddenObjects,
  includeUnscannedObjects,
  connectedStudents
}) {
  function getClassVotesForMapContextualData(dataId) {
    const votesForMapData = state.stationData.sensor.studentVotes.contentId[dataId]?.studentId ?? {};
    let classVote = 0;
    for (let student in votesForMapData) {
      if (connectedStudents.has(student)) {
        classVote += votesForMapData[student];
      }
    }
    return classVote;
  }
  return [
  // Include data from the map itself
  ...(map.data?.map(data => {
    const media = data.url && (data.type === 'IMAGE' || data.type === 'VIDEO' || data.type === 'AUDIO') ?
    // $FlowIgnore[incompatible-type] VIDEO, IMAGE, or AUDIO all work for `type` here
    [{
      type: data.type,
      url: data.url
    }] : [];
    return {
      id: data._id,
      title: map.name,
      text: data.text,
      media,
      relevance: data.relevance,
      icon: map.backgroundImage || null,
      dataSource: {
        type: 'MAP',
        mapId: map._id
      },
      studentVote: typeof entity === 'object' && entity.studentId ? state.stationData.sensor.studentVotes.contentId[data._id]?.studentId[entity.studentId] ?? null : null,
      classVote: getClassVotesForMapContextualData(data._id)
    };
  }) || []),
  // Include data from objects on the map
  ...map.objects.other.filter(object => {
    const objectInfo = state.mapInfo[map._id]?.objectInfo[object._id];
    // We don't know anything about this object, so we don't want to filter it out
    if (!objectInfo) {
      return true;
    }
    // filter out hidden objects
    if (!includeHiddenObjects && objectInfo.isHidden) {
      return false;
    }
    // filter out unscanned objects
    if (!includeUnscannedObjects && !objectInfo.actionData.SCAN.complete) {
      return false;
    }
    return true;
  }).flatMap(object => object.objectContent.map(content => {
    const media = content.url && (content.type === 'IMAGE' || content.type === 'VIDEO' || content.type === 'AUDIO') ?
    // $FlowIgnore[incompatible-type] VIDEO, IMAGE, or AUDIO all work for `type` here
    [{
      type: content.type,
      url: content.url
    }] : [];
    return {
      id: content._id,
      title: object.name,
      subtitle: object.subtitle,
      text: content.text,
      media,
      relevance: content.relevance,
      icon: object.icon?.url || null,
      dataSource: {
        type: 'MAP_OBJECT',
        mapId: map._id,
        mapObjectId: object._id
      },
      studentVote: typeof entity === 'object' && entity.studentId ? state.stationData.sensor.studentVotes.contentId[content._id]?.studentId[entity.studentId] ?? null : null,
      classVote: getClassVotesForMapContextualData(content._id)
    };
  }))];
}

/**
 * Gets contextual data that should be available to `entity` for the given `canvasId`. This includes shared data from the canvas, as well as data from the specific issue(s)
 * assigned to the `entity`. If `entity` is `'ANY'`, then all data from the canvas is returned.
 */
function getContextualDataForCreativeCanvas({
  state,
  canvasId,
  entity,
  maps,
  connectedStudents
}) {
  const canvas = state.stationData.creativeCanvas?.canvases[canvasId];
  const issuesList = Object.keys(canvas.canvasData.issues);
  const issuesToGetDataFor = (() => {
    if (entity === 'ANY' ||
    // general issue, applies to all students
    issuesList.length === 1) {
      return issuesList.map(issueId => canvas.canvasData.issues[issueId]);
    }
    const teamId = entity.teamId || entity.studentId && getTeamForStudent(state, entity.studentId)?.id;
    if (!teamId) return [];
    const documentData = canvas.teams.find(team => team.teamId === teamId);
    if (!documentData) {
      return [];
    }
    const issue = canvas.canvasData.issues[documentData.issueId];
    return issue ? [issue] : [];
  })();
  const extraContextualData = [];
  const baseContextualData = [
  // Include shared data from the canvas
  ...canvas.canvasData.data,
  // Include data from the issues assigned to this entity
  ...issuesToGetDataFor.map(issue => issue.data.map(data => ({
    ...data,
    issueId: issue.id
  }))).flat()].flatMap(data => {
    const {
      contextualData,
      extraContextualData: localExtraContextualData
    } = getContextualDataFromCreativeCanvasData(data, {
      state,
      maps,
      entity,
      canvasId,
      connectedStudents
    });
    if (localExtraContextualData) {
      extraContextualData.push(...localExtraContextualData);
    }
    return contextualData;
  });
  return {
    base: baseContextualData,
    extra: extraContextualData
  };
}

/**
 * Converts the given `creativeCanvasData` into `StudentSpecificContextualDataEntry` objects. If the `creativeCanvasData` is a reference to a map,
 * there could be "extra contextual data" that is not on the map itself. This extra data is returned in the `extraContextualData` array.
 *
 * @returns `contextualData` - The contextual data associated with the given `creativeCanvasData`
 * 		`extraContextualData` - Contextual data for the given `creativeCanvasData` that is found in `state.extraContextualData`
 */
function getContextualDataFromCreativeCanvasData(creativeCanvasData, {
  state,
  maps,
  entity,
  canvasId,
  connectedStudents
}) {
  if (creativeCanvasData.type === 'MAP') {
    const map = maps.find(map => map._id === creativeCanvasData.mapId);
    if (!map) return {
      contextualData: []
    };
    const mapContextualData = getContextualData(state, {
      type: 'MAP',
      map,
      includeUnscannedObjects: true
    }, entity);
    return {
      contextualData: mapContextualData.base,
      extraContextualData: mapContextualData.extra
    };
  }
  const votesForData = state.stationData.creativeCanvas.canvases[canvasId].relevancyVotes.dataId[creativeCanvasData._id]?.studentId ?? {};
  let classVote = 0;
  for (let studentId in votesForData) {
    if (connectedStudents.has(studentId)) {
      classVote += votesForData[studentId];
    }
  }
  const dataSource = {
    type: 'CREATIVE_CANVAS',
    canvasActionId: canvasId
  };
  if (creativeCanvasData.issueId) {
    dataSource.issueId = creativeCanvasData.issueId;
  }
  const media = creativeCanvasData.type === 'TEXT' ? [] :
  // $FlowIgnore[incompatible-type] VIDEO, IMAGE, or AUDIO all work for `type` here
  [{
    type: creativeCanvasData.type,
    url: creativeCanvasData.url
  }];
  return {
    contextualData: {
      id: creativeCanvasData._id,
      title: creativeCanvasData.name || 'Relevant Data',
      text: creativeCanvasData.text,
      media,
      relevance: creativeCanvasData.relevance,
      icon: null,
      dataSource,
      studentVote: typeof entity === 'object' && entity.studentId && votesForData[entity.studentId] || null,
      classVote
    }
  };
}